import {
    Accordion,
    AccordionButton,
    AccordionIcon,
    AccordionItem,
    AccordionPanel,
    Box,
    Card,
    CardBody,
    SimpleGrid,
    Stack,
    Stat,
    StatHelpText,
    StatLabel,
    StatNumber,
    Text,
} from '@chakra-ui/react';
import { createColumnHelper } from '@tanstack/react-table';
import uniq from 'lodash/uniq';
import { useMemo, useState } from 'react';

import { AssetClass, TradePlanDetailType } from '@/cip/_openapi/portfolio-optimizer';
import { useCustomAssetLifecycle } from '@/cip/api/optimization';
import { AssetClassTag } from '@/cip/widgets/AssetClassTag';
import { PageSpinner } from '@/cip/widgets/PageSpinner';
import { SegmentedControls } from '@/cip/widgets/SegmentedControls';
import {
    baseCol,
    booleanCol,
    dateCol,
    moneyCol,
    numberCol,
    percentageCol,
    textCol,
} from '@/cip/widgets/Table/columnHelpers/colDef';
import { formatCurrency, formatNumber } from '@/utils/number-formatter';
import { getAllocationTypeLabel } from 'utils';

import { PositionLifeCycleTable } from '../PositionLifeCycle/PositionLifeCycleTable';
import { DetailsBlock } from './DetailsBlock';
import {
    BuyConstraintDebug,
    CustomAssetDebug,
    ReplacementSolverDebug,
    RestrictionDetail,
    SellConstraintClosedLot,
    SellConstraintOpenLot,
    TradeDetailRecord,
    TradeQuantityDetail,
} from './tradeDetailsModel';

const ALL_ALLOCATION_TYPES = '__ALL__';

type RowWithAllocationType = {
    allocationType?: string | null;
};

export const CustomAssetDetails = ({
    optimizationId,
    pId,
    tradePlanDetails,
}: {
    optimizationId: number;
    pId: string;
    tradePlanDetails: Partial<Record<TradePlanDetailType, TradeDetailRecord>>;
}) => {
    const debug = tradePlanDetails[TradePlanDetailType.CUSTOM_ASSET_DEBUG]?.detailJson;
    const replacementSolverDebug = tradePlanDetails[TradePlanDetailType.REPLACEMENT_SOLVER_DEBUG]?.detailJson;
    const restrictionDetail = tradePlanDetails[TradePlanDetailType.RESTRICTION_DETAIL]?.detailJson;
    const buyConstraintDebug = tradePlanDetails[TradePlanDetailType.BUY_CONSTRAINT_DEBUG]?.detailJson;
    const sellConstraintOpenLot = tradePlanDetails[TradePlanDetailType.SELL_CONSTRAINT_OPEN_LOT]?.detailJson;
    const sellConstraintClosedLot = tradePlanDetails[TradePlanDetailType.SELL_CONSTRAINT_CLOSED_LOT]?.detailJson;
    const tradeQuantityDetail = tradePlanDetails[TradePlanDetailType.TRADE_QUANTITY_DETAIL]?.detailJson;

    const [selectedRowAllocationType, setSelectedRowAllocationType] = useState<string>(ALL_ALLOCATION_TYPES);

    const tabValues = useMemo(() => {
        const rowAllocationTypes = uniq((debug ?? []).map((row) => row.allocationType).filter(Boolean)).sort();
        return [ALL_ALLOCATION_TYPES, ...rowAllocationTypes];
    }, [debug]);

    const {
        filteredDebug,
        filteredReplacementSolverDebug,
        filteredRestrictionDetail,
        filteredBuyConstraintDebug,
        filteredSellConstraintOpenLot,
        filteredSellConstraintClosedLot,
        filteredTradeQuantityDetail,
    } = useMemo(() => {
        const filterByRowAllocationType = <T extends RowWithAllocationType>(rows?: T[]): T[] => {
            if (selectedRowAllocationType === ALL_ALLOCATION_TYPES) {
                return rows ?? [];
            }

            return (rows ?? []).filter((row) => row.allocationType === selectedRowAllocationType);
        };

        return {
            filteredDebug: filterByRowAllocationType(debug),
            filteredReplacementSolverDebug: filterByRowAllocationType(replacementSolverDebug),
            filteredRestrictionDetail: filterByRowAllocationType(restrictionDetail),
            filteredBuyConstraintDebug: filterByRowAllocationType(buyConstraintDebug),
            filteredSellConstraintOpenLot: filterByRowAllocationType(sellConstraintOpenLot),
            filteredSellConstraintClosedLot: filterByRowAllocationType(sellConstraintClosedLot),
            filteredTradeQuantityDetail: filterByRowAllocationType(tradeQuantityDetail),
        };
    }, [
        selectedRowAllocationType,
        debug,
        replacementSolverDebug,
        restrictionDetail,
        buyConstraintDebug,
        sellConstraintOpenLot,
        sellConstraintClosedLot,
        tradeQuantityDetail,
    ]);

    const rowAllocationTypeTabs = tabValues.map((value) => ({
        value,
        label: value === ALL_ALLOCATION_TYPES ? 'All' : getAllocationTypeLabel(value),
    }));

    const showSummary = selectedRowAllocationType === ALL_ALLOCATION_TYPES;

    return (
        <Stack gap={4}>
            <SegmentedControls
                onClick={setSelectedRowAllocationType}
                selectedValue={selectedRowAllocationType}
                options={rowAllocationTypeTabs}
            />

            {showSummary && (
                <CustomAssetSummary
                    sellConstraintOpenLots={filteredSellConstraintOpenLot as SellConstraintOpenLot[]}
                    tradeQuantityDetails={filteredTradeQuantityDetail as TradeQuantityDetail[]}
                />
            )}

            <Accordion allowMultiple>
                <PositionLifeCycleSection optimizationId={optimizationId} pId={pId} />
                {filteredDebug.length > 0 && (
                    <DetailsBlock
                        data={filteredDebug as CustomAssetDebug[]}
                        columns={debugColumns}
                        columnSettingsKey="customAssetDetailsDebug"
                        title="Custom Asset"
                    />
                )}
                {filteredReplacementSolverDebug.length > 0 && (
                    <DetailsBlock
                        data={filteredReplacementSolverDebug as ReplacementSolverDebug[]}
                        columns={replacementSolverDebugColumns}
                        columnSettingsKey="customAssetReplacementSolverDebug"
                        title="Replacement Solver"
                    />
                )}
                {filteredRestrictionDetail.length > 0 && (
                    <DetailsBlock
                        data={filteredRestrictionDetail as RestrictionDetail[]}
                        columns={restrictionDetailColumns}
                        columnSettingsKey="customAssetRestrictionDetail"
                        title="Restriction Details"
                    />
                )}
                {filteredBuyConstraintDebug.length > 0 && (
                    <DetailsBlock
                        data={filteredBuyConstraintDebug as BuyConstraintDebug[]}
                        columns={buyConstraintDebugColumns}
                        columnSettingsKey="customAssetBuyConstraintDebug"
                        title="Buy Constraint"
                    />
                )}
                {filteredSellConstraintOpenLot.length > 0 && (
                    <DetailsBlock
                        data={filteredSellConstraintOpenLot as SellConstraintOpenLot[]}
                        columns={sellConstraintOpenLotColumns}
                        columnSettingsKey="customAssetSellConstraintOpenLot"
                        title="Sell Constraint Open Lot"
                    />
                )}
                {filteredSellConstraintClosedLot.length > 0 && (
                    <DetailsBlock
                        data={filteredSellConstraintClosedLot as SellConstraintClosedLot[]}
                        columns={sellConstraintClosedLotColumns}
                        columnSettingsKey="customAssetSellConstraintClosedLot"
                        title="Sell Constraint Closed Lot"
                    />
                )}
                {filteredTradeQuantityDetail.length > 0 && (
                    <DetailsBlock
                        data={filteredTradeQuantityDetail as TradeQuantityDetail[]}
                        columns={tradeQuantityDetailColumns}
                        columnSettingsKey="customAssetTradeQuantityDetail"
                        title="Trade Quantity Detail"
                    />
                )}
            </Accordion>
        </Stack>
    );
};

const PositionLifeCycleSection = ({ optimizationId, pId }: { optimizationId: number; pId: string }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const { data: lifecycle, isPending, isError } = useCustomAssetLifecycle(optimizationId, pId, isExpanded);

    return (
        <AccordionItem>
            <AccordionButton background="whiteAlpha.50" onClick={() => setIsExpanded((expanded) => !expanded)}>
                <Box as="span" flex="1" textAlign="left">
                    Position LifeCycle
                </Box>
                <AccordionIcon />
            </AccordionButton>
            <AccordionPanel pb={4}>
                {isExpanded &&
                    (isPending ? (
                        <PageSpinner />
                    ) : isError || !lifecycle ? (
                        <Text color="gray.400">
                            <i>No lifecycle data available for this position</i>
                        </Text>
                    ) : (
                        <PositionLifeCycleTable report={lifecycle} />
                    ))}
            </AccordionPanel>
        </AccordionItem>
    );
};

const CustomAssetSummary = ({
    sellConstraintOpenLots,
    tradeQuantityDetails,
}: {
    sellConstraintOpenLots: SellConstraintOpenLot[];
    tradeQuantityDetails: TradeQuantityDetail[];
}) => {
    const capitalGainsSummary = useMemo(() => {
        if (sellConstraintOpenLots.length === 0) {
            return undefined;
        }

        // Realized PnL from this trade is based on what is actually sold after the tax, TLH, and
        // last-to-sell constraints - i.e. the constrained sell ratio, not the target active exposure.
        // Summing across every lot covers the whole custom asset regardless of allocation type.
        let ltRealized = 0;
        let stRealized = 0;
        for (const row of sellConstraintOpenLots) {
            const currentExposure = row.currentExposure ?? 0;
            if (currentExposure === 0) {
                continue;
            }
            const soldFraction = (row.sellConstrainedActiveExposure ?? 0) / currentExposure;
            const realizedPnl = soldFraction * (row.effectiveUnrealizedPnl ?? 0);
            if (row.isLongTerm) {
                ltRealized += realizedPnl;
            } else {
                stRealized += realizedPnl;
            }
        }

        // Realized YTD is carried per portfolio (p_id) on every lot, so take one value per portfolio.
        const ltYtdByPId = new Map<string, number>();
        const stYtdByPId = new Map<string, number>();
        for (const row of sellConstraintOpenLots) {
            ltYtdByPId.set(row.pId, row.ltPnlRealizedYtd ?? 0);
            stYtdByPId.set(row.pId, row.stPnlRealizedYtd ?? 0);
        }
        const ltPnlRealizedYtd = [...ltYtdByPId.values()].reduce((sum, value) => sum + value, 0);
        const stPnlRealizedYtd = [...stYtdByPId.values()].reduce((sum, value) => sum + value, 0);

        return {
            longTerm: { result: ltRealized, realizedYtd: ltPnlRealizedYtd, inclYtd: ltRealized + ltPnlRealizedYtd },
            shortTerm: { result: stRealized, realizedYtd: stPnlRealizedYtd, inclYtd: stRealized + stPnlRealizedYtd },
        };
    }, [sellConstraintOpenLots]);

    const tradeSummary = useMemo(() => {
        if (tradeQuantityDetails.length === 0) {
            return undefined;
        }

        let numberOfTrades = 0;
        let numberOfShares = 0;
        let turnover = 0;

        for (const row of tradeQuantityDetails) {
            const tradeQuantity = row.tradeQuantity ?? 0;
            if (tradeQuantity !== 0) {
                numberOfTrades += 1;
            }
            numberOfShares += Math.abs(tradeQuantity);
            turnover += tradeQuantity * (row.sizingPrice ?? 0);
        }

        return { numberOfTrades, numberOfShares, turnover };
    }, [tradeQuantityDetails]);

    if (!capitalGainsSummary || !tradeSummary) {
        return null;
    }

    return (
        <Card size="sm">
            <CardBody>
                <SimpleGrid columns={3} gap={2}>
                    <Stat>
                        <StatLabel>Capital Gains (Long Term)</StatLabel>
                        <StatNumber>{formatCurrency(capitalGainsSummary.longTerm.result)}</StatNumber>
                        <StatHelpText>
                            Realized YTD: {formatCurrency(capitalGainsSummary.longTerm.realizedYtd)}
                            <br />
                            Incl. YTD: {formatCurrency(capitalGainsSummary.longTerm.inclYtd)}
                        </StatHelpText>
                    </Stat>
                    <Stat>
                        <StatLabel>Capital Gains (Short Term)</StatLabel>
                        <StatNumber>{formatCurrency(capitalGainsSummary.shortTerm.result)}</StatNumber>
                        <StatHelpText>
                            Realized YTD: {formatCurrency(capitalGainsSummary.shortTerm.realizedYtd)}
                            <br />
                            Incl. YTD: {formatCurrency(capitalGainsSummary.shortTerm.inclYtd)}
                        </StatHelpText>
                    </Stat>
                    <Stat>
                        <StatLabel>Turnover</StatLabel>
                        <StatNumber>{formatCurrency(tradeSummary.turnover)}</StatNumber>
                        <StatHelpText>
                            Nr of Trades: {formatNumber(tradeSummary.numberOfTrades)}
                            <br />
                            Nr of Shares: {formatNumber(tradeSummary.numberOfShares)}
                        </StatHelpText>
                    </Stat>
                </SimpleGrid>
            </CardBody>
        </Card>
    );
};

const debugColumnHelper = createColumnHelper<CustomAssetDebug>();
const debugColumns = [
    debugColumnHelper.accessor('sId', textCol({ id: 'sId' })),
    debugColumnHelper.accessor('targetWeight', percentageCol({ id: 'targetWeight' })),
    debugColumnHelper.accessor('sumTargetWeight', percentageCol({ id: 'sumTargetWeight' })),
    debugColumnHelper.accessor('normalizedTargetWeight', percentageCol({ id: 'normalizedTargetWeight' })),
    debugColumnHelper.accessor('sumTargetExposure', moneyCol({ id: 'sumTargetExposure' })),
    debugColumnHelper.accessor('originalTargetExposure', moneyCol({ id: 'originalTargetExposure' })),
    debugColumnHelper.accessor('currentExposure', moneyCol({ id: 'currentExposure' })),
    debugColumnHelper.accessor('buyRestriction', textCol({ id: 'buyRestriction' })),
    debugColumnHelper.accessor('sellRestriction', textCol({ id: 'sellRestriction' })),
    debugColumnHelper.accessor('replacementAwareTargetExposure', moneyCol({ id: 'replacementAwareTargetExposure' })),
    debugColumnHelper.accessor('restrictionAwareTargetExposure', moneyCol({ id: 'restrictionAwareTargetExposure' })),
    debugColumnHelper.accessor('buyConstrainedTargetExposure', moneyCol({ id: 'buyConstrainedTargetExposure' })),
    debugColumnHelper.accessor('originalActiveExposure', moneyCol({ id: 'originalActiveExposure' })),
    debugColumnHelper.accessor('harvestableExposure', moneyCol({ id: 'harvestableExposure' })),
    debugColumnHelper.accessor('tlhActiveExposure', moneyCol({ id: 'tlhActiveExposure' })),
    debugColumnHelper.accessor('sellConstrainedActiveExposure', moneyCol({ id: 'sellConstrainedActiveExposure' })),
    debugColumnHelper.accessor('bidPrice', moneyCol({ id: 'bidPrice' })),
    debugColumnHelper.accessor('askPrice', moneyCol({ id: 'askPrice' })),
    debugColumnHelper.accessor('orderPrice', moneyCol({ id: 'orderPrice' })),
    debugColumnHelper.accessor('assetClass', {
        ...baseCol({ id: 'assetClass' }),
        cell: ({ getValue }) => <AssetClassTag assetClass={getValue() as AssetClass} />,
    }),
    debugColumnHelper.accessor('accruedInterest', numberCol({ id: 'accruedInterest' })),
    debugColumnHelper.accessor('priceSource', textCol({ id: 'priceSource' })),
    debugColumnHelper.accessor('priceMultiplier', numberCol({ id: 'priceMultiplier' })),
    debugColumnHelper.accessor('contractMultiplier', numberCol({ id: 'contractMultiplier' })),
    debugColumnHelper.accessor('quantity', numberCol({ id: 'quantity' })),
    debugColumnHelper.accessor('normalizedCurrentQuantity', numberCol({ id: 'normalizedCurrentQuantity' })),
    debugColumnHelper.accessor('sizingPrice', moneyCol({ id: 'sizingPrice' })),
    debugColumnHelper.accessor('tradeQuantityContinuous', numberCol({ id: 'tradeQuantityContinuous' })),
    debugColumnHelper.accessor('tradeQuantity', numberCol({ id: 'tradeQuantity' })),
    debugColumnHelper.accessor('isActive', booleanCol({ id: 'isActive' })),
];

const replacementSolverDebugColumnHelper = createColumnHelper<ReplacementSolverDebug>();
const replacementSolverDebugColumns = [
    replacementSolverDebugColumnHelper.accessor('tailSId', textCol({ id: 'tailSId' })),
    replacementSolverDebugColumnHelper.accessor('headSId', textCol({ id: 'headSId' })),
    replacementSolverDebugColumnHelper.accessor('tailSupply', moneyCol({ id: 'tailSupply' })),
    replacementSolverDebugColumnHelper.accessor('headSupply', moneyCol({ id: 'headSupply' })),
    replacementSolverDebugColumnHelper.accessor('flow', moneyCol({ id: 'flow' })),
    replacementSolverDebugColumnHelper.accessor('capacity', moneyCol({ id: 'capacity' })),
    replacementSolverDebugColumnHelper.accessor('unitCost', numberCol({ id: 'unitCost' })),
];

const restrictionDetailColumnHelper = createColumnHelper<RestrictionDetail>();
const restrictionDetailColumns = [
    restrictionDetailColumnHelper.accessor('sId', textCol({ id: 'sId' })),
    restrictionDetailColumnHelper.accessor('currentExposure', moneyCol({ id: 'currentExposure' })),
    restrictionDetailColumnHelper.accessor('targetExposure', moneyCol({ id: 'targetExposure' })),
    restrictionDetailColumnHelper.accessor('buyRestriction', textCol({ id: 'buyRestriction' })),
    restrictionDetailColumnHelper.accessor('sellRestriction', textCol({ id: 'sellRestriction' })),
    restrictionDetailColumnHelper.accessor('restrictedTargetExposure', moneyCol({ id: 'restrictedTargetExposure' })),
    restrictionDetailColumnHelper.accessor('targetExposureShortfall', moneyCol({ id: 'targetExposureShortfall' })),
    restrictionDetailColumnHelper.accessor('replacementTargetExposure', moneyCol({ id: 'replacementTargetExposure' })),
    restrictionDetailColumnHelper.accessor(
        'replacementAdjustedTargetExposure',
        moneyCol({ id: 'replacementAdjustedTargetExposure' }),
    ),
    restrictionDetailColumnHelper.accessor(
        'sumUnrestrictedTargetExposure',
        moneyCol({ id: 'sumUnrestrictedTargetExposure' }),
    ),
    restrictionDetailColumnHelper.accessor(
        'sumReplacementAdjustedTargetExposure',
        moneyCol({ id: 'sumReplacementAdjustedTargetExposure' }),
    ),
    restrictionDetailColumnHelper.accessor(
        'netTargetExposureShortfall',
        moneyCol({ id: 'netTargetExposureShortfall' }),
    ),
    restrictionDetailColumnHelper.accessor('eligibleTargetExposure', moneyCol({ id: 'eligibleTargetExposure' })),
    restrictionDetailColumnHelper.accessor('sumEligibleTargetExposure', moneyCol({ id: 'sumEligibleTargetExposure' })),
    restrictionDetailColumnHelper.accessor('redistributionWeight', percentageCol({ id: 'redistributionWeight' })),
    restrictionDetailColumnHelper.accessor(
        'restrictionAwareTargetExposure',
        moneyCol({ id: 'restrictionAwareTargetExposure' }),
    ),
];

const buyConstraintDebugColumnHelper = createColumnHelper<BuyConstraintDebug>();
const buyConstraintDebugColumns = [
    buyConstraintDebugColumnHelper.accessor('sId', textCol({ id: 'sId' })),
    buyConstraintDebugColumnHelper.accessor('currentExposure', moneyCol({ id: 'currentExposure' })),
    buyConstraintDebugColumnHelper.accessor('targetExposure', moneyCol({ id: 'targetExposure' })),
    buyConstraintDebugColumnHelper.accessor('maxBuyConstraint', moneyCol({ id: 'maxBuyConstraint' })),
    buyConstraintDebugColumnHelper.accessor('currentExposurePercent', percentageCol({ id: 'currentExposurePercent' })),
    buyConstraintDebugColumnHelper.accessor('minExposurePercent', percentageCol({ id: 'minExposurePercent' })),
    buyConstraintDebugColumnHelper.accessor('nextExposurePercent', percentageCol({ id: 'nextExposurePercent' })),
    buyConstraintDebugColumnHelper.accessor('cumsumCurrentExposure', moneyCol({ id: 'cumsumCurrentExposure' })),
    buyConstraintDebugColumnHelper.accessor('cumsumTargetExposure', moneyCol({ id: 'cumsumTargetExposure' })),
    buyConstraintDebugColumnHelper.accessor('cumsumExposureToNext', moneyCol({ id: 'cumsumExposureToNext' })),
    buyConstraintDebugColumnHelper.accessor('commonFillPercent', percentageCol({ id: 'commonFillPercent' })),
    buyConstraintDebugColumnHelper.accessor('sumExposureToFill', moneyCol({ id: 'sumExposureToFill' })),
    buyConstraintDebugColumnHelper.accessor('inFillGroup', booleanCol({ id: 'inFillGroup' })),
    buyConstraintDebugColumnHelper.accessor('sumFilledTargetExposure', moneyCol({ id: 'sumFilledTargetExposure' })),
    buyConstraintDebugColumnHelper.accessor('excessExposure', moneyCol({ id: 'excessExposure' })),
    buyConstraintDebugColumnHelper.accessor('excessFillPercent', percentageCol({ id: 'excessFillPercent' })),
    buyConstraintDebugColumnHelper.accessor('finalFillPercent', percentageCol({ id: 'finalFillPercent' })),
    buyConstraintDebugColumnHelper.accessor(
        'buyConstrainedTargetExposure',
        moneyCol({ id: 'buyConstrainedTargetExposure' }),
    ),
];

const sellConstraintOpenLotColumnsHelper = createColumnHelper<SellConstraintOpenLot>();
const sellConstraintOpenLotColumns = [
    sellConstraintOpenLotColumnsHelper.accessor('sId', textCol({ id: 'sId' })),
    sellConstraintOpenLotColumnsHelper.accessor('openLotId', textCol({ id: 'openLotId' })),
    sellConstraintOpenLotColumnsHelper.accessor('accountType', textCol({ id: 'accountType' })),
    sellConstraintOpenLotColumnsHelper.accessor('currentPositionExposure', moneyCol({ id: 'currentPositionExposure' })),
    sellConstraintOpenLotColumnsHelper.accessor(
        'originalActivePositionExposure',
        moneyCol({ id: 'originalActivePositionExposure' }),
    ),
    sellConstraintOpenLotColumnsHelper.accessor(
        'tlhActivePositionExposure',
        moneyCol({ id: 'tlhActivePositionExposure' }),
    ),
    sellConstraintOpenLotColumnsHelper.accessor('currentExposure', moneyCol({ id: 'currentExposure' })),
    sellConstraintOpenLotColumnsHelper.accessor('lotQuantity', numberCol({ id: 'lotQuantity' })),
    sellConstraintOpenLotColumnsHelper.accessor('costBasis', moneyCol({ id: 'costBasis' })),
    sellConstraintOpenLotColumnsHelper.accessor('costBasisPerShare', moneyCol({ id: 'costBasisPerShare' })),
    sellConstraintOpenLotColumnsHelper.accessor('hifoCumsumLotExposure', moneyCol({ id: 'hifoCumsumLotExposure' })),
    sellConstraintOpenLotColumnsHelper.accessor(
        'hifoCumsumPriorLotExposure',
        moneyCol({ id: 'hifoCumsumPriorLotExposure' }),
    ),
    sellConstraintOpenLotColumnsHelper.accessor('activeLotExposure', moneyCol({ id: 'activeLotExposure' })),
    sellConstraintOpenLotColumnsHelper.accessor('isLongTerm', booleanCol({ id: 'isLongTerm' })),
    sellConstraintOpenLotColumnsHelper.accessor(
        'longTermCapGainsTaxRate',
        percentageCol({ id: 'longTermCapGainsTaxRate' }),
    ),
    sellConstraintOpenLotColumnsHelper.accessor(
        'shortTermCapGainsTaxRate',
        percentageCol({ id: 'shortTermCapGainsTaxRate' }),
    ),
    sellConstraintOpenLotColumnsHelper.accessor('ownTermTaxRate', percentageCol({ id: 'ownTermTaxRate' })),
    sellConstraintOpenLotColumnsHelper.accessor('crossTermTaxRate', percentageCol({ id: 'crossTermTaxRate' })),
    sellConstraintOpenLotColumnsHelper.accessor('unrealizedPnl', moneyCol({ id: 'unrealizedPnl' })),
    sellConstraintOpenLotColumnsHelper.accessor('effectiveUnrealizedPnl', moneyCol({ id: 'effectiveUnrealizedPnl' })),
    sellConstraintOpenLotColumnsHelper.accessor('targetPositionExposure', moneyCol({ id: 'targetPositionExposure' })),
    sellConstraintOpenLotColumnsHelper.accessor('meanPositionExposure', moneyCol({ id: 'meanPositionExposure' })),
    sellConstraintOpenLotColumnsHelper.accessor('normalizedActiveRatio', numberCol({ id: 'normalizedActiveRatio' })),
    sellConstraintOpenLotColumnsHelper.accessor('scaledActiveRatio', numberCol({ id: 'scaledActiveRatio' })),
    sellConstraintOpenLotColumnsHelper.accessor('activeRatioMultiplier', numberCol({ id: 'activeRatioMultiplier' })),
    sellConstraintOpenLotColumnsHelper.accessor('taxCost', moneyCol({ id: 'taxCost' })),
    sellConstraintOpenLotColumnsHelper.accessor('unitTaxCost', numberCol({ id: 'unitTaxCost' })),
    sellConstraintOpenLotColumnsHelper.accessor('tier', numberCol({ id: 'tier' })),
    sellConstraintOpenLotColumnsHelper.accessor('score', numberCol({ id: 'score' })),
    sellConstraintOpenLotColumnsHelper.accessor('cumminScore', numberCol({ id: 'cumminScore' })),
    sellConstraintOpenLotColumnsHelper.accessor('sellRatio', numberCol({ id: 'sellRatio' })),
    sellConstraintOpenLotColumnsHelper.accessor('stPnlRealizedYtd', moneyCol({ id: 'stPnlRealizedYtd' })),
    sellConstraintOpenLotColumnsHelper.accessor('ltPnlRealizedYtd', moneyCol({ id: 'ltPnlRealizedYtd' })),
    sellConstraintOpenLotColumnsHelper.accessor('activePnl', moneyCol({ id: 'activePnl' })),
    sellConstraintOpenLotColumnsHelper.accessor('activeGains', moneyCol({ id: 'activeGains' })),
    sellConstraintOpenLotColumnsHelper.accessor('activeStPnl', moneyCol({ id: 'activeStPnl' })),
    sellConstraintOpenLotColumnsHelper.accessor('activeLtPnl', moneyCol({ id: 'activeLtPnl' })),
    sellConstraintOpenLotColumnsHelper.accessor('cumsumStPnl', moneyCol({ id: 'cumsumStPnl' })),
    sellConstraintOpenLotColumnsHelper.accessor('cumsumLtPnl', moneyCol({ id: 'cumsumLtPnl' })),
    sellConstraintOpenLotColumnsHelper.accessor('cumsumPriorStPnl', moneyCol({ id: 'cumsumPriorStPnl' })),
    sellConstraintOpenLotColumnsHelper.accessor('cumsumPriorLtPnl', moneyCol({ id: 'cumsumPriorLtPnl' })),
    sellConstraintOpenLotColumnsHelper.accessor('cumsumPriorOwnTermPnl', moneyCol({ id: 'cumsumPriorOwnTermPnl' })),
    sellConstraintOpenLotColumnsHelper.accessor('cumsumPriorCrossTermPnl', moneyCol({ id: 'cumsumPriorCrossTermPnl' })),
    sellConstraintOpenLotColumnsHelper.accessor('priorNetLosses', moneyCol({ id: 'priorNetLosses' })),
    sellConstraintOpenLotColumnsHelper.accessor(
        'priorCrossNettedOwnTermLosses',
        moneyCol({ id: 'priorCrossNettedOwnTermLosses' }),
    ),
    sellConstraintOpenLotColumnsHelper.accessor('lossCoveredGains', moneyCol({ id: 'lossCoveredGains' })),
    sellConstraintOpenLotColumnsHelper.accessor('crossTermGains', moneyCol({ id: 'crossTermGains' })),
    sellConstraintOpenLotColumnsHelper.accessor('ownTermGains', moneyCol({ id: 'ownTermGains' })),
    sellConstraintOpenLotColumnsHelper.accessor('crossTermTax', moneyCol({ id: 'crossTermTax' })),
    sellConstraintOpenLotColumnsHelper.accessor('ownTermTax', moneyCol({ id: 'ownTermTax' })),
    sellConstraintOpenLotColumnsHelper.accessor('dominantTermTaxRate', percentageCol({ id: 'dominantTermTaxRate' })),
    sellConstraintOpenLotColumnsHelper.accessor('totalTax', moneyCol({ id: 'totalTax' })),
    sellConstraintOpenLotColumnsHelper.accessor('taxBudget', moneyCol({ id: 'taxBudget' })),
    sellConstraintOpenLotColumnsHelper.accessor('taxBudgetYear', numberCol({ id: 'taxBudgetYear' })),
    sellConstraintOpenLotColumnsHelper.accessor('nav', moneyCol({ id: 'nav' })),
    sellConstraintOpenLotColumnsHelper.accessor('effectiveTaxBudget', moneyCol({ id: 'effectiveTaxBudget' })),
    sellConstraintOpenLotColumnsHelper.accessor('taxBudgetShortfall', moneyCol({ id: 'taxBudgetShortfall' })),
    sellConstraintOpenLotColumnsHelper.accessor('lotTaxBudget', moneyCol({ id: 'lotTaxBudget' })),
    sellConstraintOpenLotColumnsHelper.accessor(
        'coveredOwnTermGainsRatio',
        numberCol({ id: 'coveredOwnTermGainsRatio' }),
    ),
    sellConstraintOpenLotColumnsHelper.accessor(
        'coveredCrossTermGainsRatio',
        numberCol({ id: 'coveredCrossTermGainsRatio' }),
    ),
    sellConstraintOpenLotColumnsHelper.accessor('coveredGains', moneyCol({ id: 'coveredGains' })),
    sellConstraintOpenLotColumnsHelper.accessor('coveredTaxRatio', numberCol({ id: 'coveredTaxRatio' })),
    sellConstraintOpenLotColumnsHelper.accessor('minSellConstraint', moneyCol({ id: 'minSellConstraint' })),
    sellConstraintOpenLotColumnsHelper.accessor('isCappedAtMinSell', booleanCol({ id: 'isCappedAtMinSell' })),
    sellConstraintOpenLotColumnsHelper.accessor(
        'cumsumTaxConstrainedLotExposure',
        moneyCol({ id: 'cumsumTaxConstrainedLotExposure' }),
    ),
    sellConstraintOpenLotColumnsHelper.accessor('minSellExcessExposure', moneyCol({ id: 'minSellExcessExposure' })),
    sellConstraintOpenLotColumnsHelper.accessor('minSellExposure', moneyCol({ id: 'minSellExposure' })),
    sellConstraintOpenLotColumnsHelper.accessor('sellRestriction', textCol({ id: 'sellRestriction' })),
    sellConstraintOpenLotColumnsHelper.accessor('lastToSellRatio', numberCol({ id: 'lastToSellRatio' })),
    sellConstraintOpenLotColumnsHelper.accessor(
        'harvestableUnderweightLotExposure',
        moneyCol({ id: 'harvestableUnderweightLotExposure' }),
    ),
    sellConstraintOpenLotColumnsHelper.accessor(
        'cumsumHarvestableUnderweightLotExposure',
        moneyCol({ id: 'cumsumHarvestableUnderweightLotExposure' }),
    ),
    sellConstraintOpenLotColumnsHelper.accessor('tlhBudget', moneyCol({ id: 'tlhBudget' })),
    sellConstraintOpenLotColumnsHelper.accessor('tlhBudgetShortfall', moneyCol({ id: 'tlhBudgetShortfall' })),
    sellConstraintOpenLotColumnsHelper.accessor(
        'tlhBudgetCoveredLotExposure',
        moneyCol({ id: 'tlhBudgetCoveredLotExposure' }),
    ),
    sellConstraintOpenLotColumnsHelper.accessor('tlhSellRatio', numberCol({ id: 'tlhSellRatio' })),
    sellConstraintOpenLotColumnsHelper.accessor('constrainedSellRatio', numberCol({ id: 'constrainedSellRatio' })),
    sellConstraintOpenLotColumnsHelper.accessor(
        'sellConstrainedActiveExposure',
        moneyCol({ id: 'sellConstrainedActiveExposure' }),
    ),
];

const sellConstraintClosedLotColumnsHelper = createColumnHelper<SellConstraintClosedLot>();
const sellConstraintClosedLotColumns = [
    sellConstraintClosedLotColumnsHelper.accessor('sId', textCol({ id: 'sId' })),
    sellConstraintClosedLotColumnsHelper.accessor('closedLotId', textCol({ id: 'closedLotId' })),
    sellConstraintClosedLotColumnsHelper.accessor('isLongTerm', booleanCol({ id: 'isLongTerm' })),
    sellConstraintClosedLotColumnsHelper.accessor('pnlRealized', moneyCol({ id: 'pnlRealized' })),
    sellConstraintClosedLotColumnsHelper.accessor('stPnlRealized', moneyCol({ id: 'stPnlRealized' })),
    sellConstraintClosedLotColumnsHelper.accessor('ltPnlRealized', moneyCol({ id: 'ltPnlRealized' })),
    sellConstraintClosedLotColumnsHelper.accessor('tradeDate', dateCol({ id: 'tradeDate' })),
];

const tradeQuantityDetailColumnHelper = createColumnHelper<TradeQuantityDetail>();
const tradeQuantityDetailColumns = [
    tradeQuantityDetailColumnHelper.accessor('sId', textCol({ id: 'sId' })),
    tradeQuantityDetailColumnHelper.accessor('sizingPrice', moneyCol({ id: 'sizingPrice' })),
    tradeQuantityDetailColumnHelper.accessor('tradeQuantityContinuous', numberCol({ id: 'tradeQuantityContinuous' })),
    tradeQuantityDetailColumnHelper.accessor('tradeQuantityInteger', numberCol({ id: 'tradeQuantityInteger' })),
    tradeQuantityDetailColumnHelper.accessor('targetQuantityContinuous', numberCol({ id: 'targetQuantityContinuous' })),
    tradeQuantityDetailColumnHelper.accessor('overweightRatio', numberCol({ id: 'overweightRatio' })),
    tradeQuantityDetailColumnHelper.accessor('underweightRatio', numberCol({ id: 'underweightRatio' })),
    tradeQuantityDetailColumnHelper.accessor('fractionalBuyQuantity', numberCol({ id: 'fractionalBuyQuantity' })),
    tradeQuantityDetailColumnHelper.accessor('fractionalBuyExposure', moneyCol({ id: 'fractionalBuyExposure' })),
    tradeQuantityDetailColumnHelper.accessor('truncatedSellExposure', moneyCol({ id: 'truncatedSellExposure' })),
    tradeQuantityDetailColumnHelper.accessor('plusOneBudget', moneyCol({ id: 'plusOneBudget' })),
    tradeQuantityDetailColumnHelper.accessor('plusOneBudgetRemaining', moneyCol({ id: 'plusOneBudgetRemaining' })),
    tradeQuantityDetailColumnHelper.accessor('plusOneApplied', booleanCol({ id: 'plusOneApplied' })),
    tradeQuantityDetailColumnHelper.accessor('minSellConstraint', moneyCol({ id: 'minSellConstraint' })),
    tradeQuantityDetailColumnHelper.accessor('realizedSellExposure', moneyCol({ id: 'realizedSellExposure' })),
    tradeQuantityDetailColumnHelper.accessor('minSellShortfall', moneyCol({ id: 'minSellShortfall' })),
    tradeQuantityDetailColumnHelper.accessor('tradeQuantity', numberCol({ id: 'tradeQuantity' })),
];
